import { Flex, Button, Image, Text, AspectRatio } from "@chakra-ui/react";
import { Squiggle170x10Icon } from "../../components/icons/Squiggle170x10Icon";
import { useIsSmallBreakpoint } from "../../utils/useBreakpoints";
import { ComicLabelsWrap } from "../../components/comicLabels/ComicLabelsWrap";
import { Artist, Comic } from "../../types";
import { useQuery, skipToken } from "@tanstack/react-query";
import { EyeIcon } from "../../components/icons/EyeIcon";
import { FunnelContextArea } from "../../components/funnel/types";
import { FollowingIcon } from "../../components/icons/FollowingIcon";
import { useToggleArtistFollow } from "../../components/cards/hooks/useToggleArtistFollow";
import { ContextualCTAButton } from "../../components/buttons/ContextualCTAButton";
import { ComicLanguageLabels } from "../../components/comicLabels/ComicLanguageLabels";
import { useNavigateToReaderWithContext } from "../reader/hooks/useNavigateToReaderWithContext";
import { NavigationTrigger } from "../reader/types";

export function Apollyon({ comicData }: { comicData?: Comic }) {
  const isSmallBreakpoint = useIsSmallBreakpoint();

  const comicCacheKey = `${comicData?.entity_type}-${comicData?.id}`;
  const { data: cachedComicData } = useQuery<Comic>({
    enabled: !!comicData,
    queryKey: [comicCacheKey],
    queryFn: skipToken,
    staleTime: Infinity,
  });

  const artistData = comicData?.artist;
  const artistCacheKey = `${artistData?.entity_type}-${artistData?.id}`;
  const { data: cachedArtistData } = useQuery<Artist>({
    enabled: !!artistData,
    queryKey: [artistCacheKey],
    queryFn: skipToken,
    staleTime: Infinity,
  });
  const { handleToggleArtistFollow } = useToggleArtistFollow(
    artistCacheKey,
    FunnelContextArea.valkyrae,
  );
  const isFollowing = cachedArtistData?.user_is_following;

  // Create navigation hooks for manga and vertical versions
  const { goToCTAUrl: goToManga } = useNavigateToReaderWithContext({
    comicToNavigate: cachedComicData,
    trigger: NavigationTrigger.specialLandingPageCard,
    area: "valkyrae",
  });

  // For the vertical toon, create a minimal release object with the hardcoded UUID
  const verticalRelease = {
    entity_type: "Release" as const,
    key: "986a67a7-7b05-4202-9e64-61715793bad7",
  };

  const { goToCTAUrl: goToVertical } = useNavigateToReaderWithContext({
    releaseToNavigate: verticalRelease as any,
    trigger: NavigationTrigger.specialLandingPageCard,
    area: "valkyrae",
  });

  return (
    <Flex flexDirection="column" gap="24px" width="100%" alignItems="center">
      <Text
        as="h2"
        variant="landingPageSectionTitle"
        textAlign="center"
        id="apollyon-section"
      >
        <span style={{ transform: "skew(-12deg)", display: "inline-block" }}>
          Apollyon
        </span>{" "}
        Ch.01 is here!
      </Text>
      <Squiggle170x10Icon width="172px" height="12px" color="blaze.blaze" />

      <Flex
        mt="16px"
        padding={isSmallBreakpoint ? "32px" : "62px"}
        background="charcoal.700"
        borderRadius="24px"
        gap="62px"
        width="100%"
        flexDirection={isSmallBreakpoint ? "column" : "row"}
      >
        <AspectRatio
          flex="1"
          ratio={5 / 7}
          width={isSmallBreakpoint ? "100%" : "auto"}
          minHeight="300px"
          role="img"
          aria-label="Apollyon comic preview pages showing fantasy world and characters"
        >
          <Flex position="relative" width="100%" height="100%">
            <Image
              borderRadius="10px"
              maxHeight={isSmallBreakpoint ? "65%" : "55%"}
              src={`${window.gc.global.assets_url}/static/images/web-ui/valkyrae/apollyonPage2.jpg`}
              filter="drop-shadow(0px 14.293px 57.172px rgba(0, 0, 0, 0.80))"
              border="2px solid"
              borderColor="transparent.white.20"
              position="absolute"
              right="0"
              bottom="32px"
              objectFit="cover"
              alt="Apollyon comic page 2 - character in mystical setting"
            />
            <Image
              borderRadius="10px"
              maxHeight={isSmallBreakpoint ? "65%" : "55%"}
              src={`${window.gc.global.assets_url}/static/images/web-ui/valkyrae/apollyonPage1.jpg`}
              border="2px solid"
              borderColor="transparent.white.20"
              filter="drop-shadow(0px 14.293px 57.172px rgba(0, 0, 0, 0.80))"
              position="absolute"
              left="0"
              top="32px"
              objectFit="cover"
              alt="Apollyon comic page 1 - introducing the fantasy world of Caelira and Chasm City"
            />
          </Flex>
        </AspectRatio>
        <Flex flex="1" flexDirection="column" gap="24px">
          <AspectRatio ratio={5 / 2} width="100%">
            <Image
              pr="10px"
              src={`${window.gc.global.assets_url}/static/images/web-ui/valkyrae/apollyon_logo.png`}
              width="100%"
              height="100%"
              alt="Apollyon - fantasy manga series logo featuring mystical typography"
              objectFit="fill"
            />
          </AspectRatio>
          <Flex
            flexDirection="column"
            gap="24px"
            alignItems={isSmallBreakpoint ? "center" : "flex-start"}
          >
            <Text
              color="blaze.blaze"
              fontFamily="Bricolage Grotesque"
              fontWeight="800"
              lineHeight="107%"
              fontSize="24px"
            >
              Ch. 01 Available in these Languages:
            </Text>
            <ComicLanguageLabels comicData={cachedComicData} />
            <Text
              color="dune.600"
              fontFamily="Roboto"
              fontWeight="500"
              lineHeight="150%"
              fontSize="15px"
            >
              In <span style={{ fontStyle: "italic" }}>APOLLYON</span>, the
              world is divided between the radiant city of Caelira, a utopia
              suspended in the clouds, and Chasm City, a grim underbelly
              encircling a vast abyss, magic is both a blessing and a curse.
              Neru, the son of Caelira’s most powerful wizard, struggles under
              the weight of his father’s legacy. Despite his immense magical
              potential, he’s never been able to cast a single spell.
              <br />
              <br />
              Thus, he was banished to Chasm City, an urban pit carved into the
              center of the world where the magicless live amongst demons. Neru
              believes his life is doomed to mediocrity… that is until he
              crosses paths with a magical entity that was believed to be
              extinct, a fallen Angel, that unlocks Neru’s ability to cast
              spells.
            </Text>
            <ComicLabelsWrap
              labels={["Manga", "Fantasy", "Magic", "Demons", "Angels"]}
            />
            <Flex gap="16px" flexWrap="wrap">
              <ContextualCTAButton
                variant="primary"
                size="lg"
                placement="bottom-start"
                dropdownOptions={[
                  {
                    id: "manga",
                    label: "Read manga",
                    ctaText: "Read",
                    ctaVariant: "primary" as const,
                    action: goToManga,
                  },
                  {
                    id: "vertical",
                    label: "Read vertical toon",
                    ctaText: "Read",
                    ctaVariant: "primary" as const,
                    action: goToVertical,
                  },
                ]}
              >
                <Flex gap="8px" alignItems="center">
                  <EyeIcon boxSize="20px" color="inherit" aria-hidden="true" />
                  Ways to read
                </Flex>
              </ContextualCTAButton>
              <Button
                variant="tertiary"
                height="48px"
                padding="14px 20px"
                borderRadius="16px"
                onClick={handleToggleArtistFollow}
                aria-label={
                  isFollowing
                    ? "Currently following Apollyon - click to unfollow"
                    : "Get notified when Apollyon is released"
                }
                aria-pressed={isFollowing}
                size="lg"
              >
                {isFollowing ? (
                  <FollowingIcon
                    boxSize="20px"
                    color="inherit"
                    aria-hidden="true"
                  />
                ) : null}
                {isFollowing ? "Following" : "Follow"}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}
